import { useState } from 'react'
import { fbt } from 'fbtee'
import { toast } from 'sonner'
import { Capacitor } from '@capacitor/core'
import { Download, Link, Loader2, ChevronRight } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useCalendarToken, DEFAULT_INCLUDE } from '../hooks/useCalendarToken'
import type { CalendarInclude } from '../hooks/useCalendarToken'
import { useGroups } from '../hooks/useGroups'
import { useFriendships } from '../hooks/useFriendships'
import { pageTitle, useDocumentTitle } from '../hooks/useDocumentTitle'
import { useTrackView } from '../hooks/useTrackView'
import { usePageLoadTrace } from '../lib/perf'
import { track, type CalendarSetting, type CalendarSubscribeMethod } from '../lib/analytics'
import { PageLayout } from '../components/PageLayout'
import { SectionHeader } from '../components/SectionHeader'
import { SettingsCard, SettingsRow as SettingsRowBase } from '../components/SettingsCard'
import { Button } from '../components/ui/button'
import { Switch } from '../components/ui/switch'
import { writeClipboard } from '../lib/clipboard'
import { classifyError } from '../lib/errors'
import { toastError } from '../lib/toast'
import { copyFailedToast } from '../lib/commonStrings'

type Status = 'going' | 'interested'

function hasStatus(arr: Status[] | undefined, s: Status): boolean {
  return arr?.includes(s) ?? false
}

function toggleStatus(arr: Status[] | undefined, s: Status): Status[] {
  const current = arr ?? []
  return current.includes(s) ? current.filter(v => v !== s) : [...current, s]
}

export default function CalendarSyncPage() {
  useDocumentTitle(
    pageTitle(fbt('Calendar sync', 'Browser tab title for the calendar sync page')),
  )
  const { user } = useAuth()
  const { url, loading, include, updateInclude } = useCalendarToken(user)
  const { groups, loading: groupsLoading } = useGroups(user?.uid ?? null)
  const { friendIds, loading: friendsLoading } = useFriendships(user?.uid ?? null)
  const [groupsExpanded, setGroupsExpanded] = useState(false)

  const webcalUrl = url?.replace(/^https?:\/\//, 'webcal://') ?? null
  // Android (native app or Chrome) has no webcal: handler, so the Apple button
  // is a dead control there — hide it. Google / Download are the Android paths.
  // The Capacitor Android WebView's UA also carries "Android", so this UA test
  // covers both the native shell and a mobile browser.
  const isAndroid = /Android/i.test(navigator.userAgent)
  const hasFriends = friendIds.length > 0
  const hasGroups = groups.length > 0
  const ready = !loading && !groupsLoading && !friendsLoading

  const groupDefault = include.groupDefault ?? DEFAULT_INCLUDE.groupDefault ?? true

  function isGroupEnabled(groupId: string): boolean {
    const explicit = include.groups?.[groupId]
    return explicit !== undefined ? explicit : groupDefault
  }

  async function handleCopy() {
    if (!url) return
    try {
      await writeClipboard(url)
    } catch (err) {
      toastError(copyFailedToast(), classifyError(err))
      return
    }
    track('calendar_subscribed', { method: 'copy_link' })
    toast.success(
      fbt(
        'Calendar URL copied — treat it like a password.',
        'Toast shown after copying calendar URL, includes privacy warning',
      ).toString(),
    )
  }

  // window.open is a no-op in the native WebView (Capacitor's WebChromeClient
  // has no onCreateWindow); a navigation is instead intercepted and handed to
  // the OS (browser / calendar app). On web, http(s) links open in a new tab
  // but custom schemes (webcal:) must navigate — window.open on a protocol URL
  // can leave a stray blank tab.
  function openExternal(target: string, method: CalendarSubscribeMethod) {
    track('calendar_subscribed', { method })
    if (Capacitor.isNativePlatform() || !/^https?:/.test(target)) {
      window.location.href = target
    } else {
      window.open(target, '_blank')
    }
  }

  function handleApple() {
    if (!webcalUrl) return
    openExternal(webcalUrl, 'apple')
  }

  function handleGoogle() {
    if (!webcalUrl) return
    openExternal(`https://calendar.google.com/calendar/render?cid=${webcalUrl}`, 'google')
  }

  function handleOutlook() {
    if (!url) return
    openExternal(
      `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(url)}&name=Faro`,
      'outlook',
    )
  }

  function handleDownloadIcs() {
    if (!url) return
    openExternal(url, 'ics_download')
  }

  function handleToggle(patch: Partial<CalendarInclude>, setting: CalendarSetting, enabled: boolean) {
    updateInclude(patch).then(() => track('calendar_settings_changed', { setting, enabled }))
  }

  function handleStatusToggle(field: 'myEvents' | 'friendEvents', status: Status) {
    const next = toggleStatus(include[field], status)
    const who = field === 'myEvents' ? 'my' : 'friend'
    handleToggle({ [field]: next }, `${who}_${status}`, next.includes(status))
  }

  function handleGroupToggle(groupId: string) {
    const enabled = !isGroupEnabled(groupId)
    handleToggle({ groups: { ...include.groups, [groupId]: enabled } }, 'group', enabled)
  }

  function handleGroupDefaultToggle() {
    handleToggle({ groupDefault: !groupDefault }, 'group_default', !groupDefault)
  }

  useTrackView('calendar_link_shown', loading ? null : {})
  usePageLoadTrace('calendar_sync_page_to_content', ready)

  return (
    <PageLayout
      back="/settings"
      title={
        <p className="font-semibold text-sm leading-none">
          <fbt desc="Heading for the calendar sync page">Calendar sync</fbt>
        </p>
      }
    >
      {!ready ? (
        <div className="flex justify-center py-8">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-6">
          <section className="space-y-3">
            <SectionHeader variant="settings">
              <fbt desc="Section heading for calendar subscription buttons">Add to calendar</fbt>
            </SectionHeader>
            <p className="text-sm text-muted-foreground">
              <fbt desc="Description of calendar sync on its dedicated page">
                Sync events from you, your friends, and your groups to your calendar app.
                Customize what's included below.
              </fbt>
            </p>

            <div className="grid grid-cols-2 gap-2">
              {!isAndroid && (
                <Button
                  variant="outline"
                  className="w-full cursor-pointer"
                  onClick={handleApple}
                >
                  <AppleIcon className="size-4" />
                  Apple
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full cursor-pointer"
                onClick={handleGoogle}
              >
                <GoogleCalendarIcon className="size-4" />
                Google
              </Button>
              <Button
                variant="outline"
                className="w-full cursor-pointer"
                onClick={handleOutlook}
              >
                <OutlookIcon className="size-4" />
                Outlook
              </Button>
              <Button
                variant="outline"
                className={`w-full cursor-pointer ${isAndroid ? 'col-span-2' : ''}`}
                onClick={handleDownloadIcs}
              >
                <Download className="size-4" />
                <fbt desc="Button to download the calendar feed as an ICS file">
                  Download
                </fbt>
              </Button>
            </div>

            <Button
              variant="ghost"
              size="sm"
              className="w-full cursor-pointer text-muted-foreground"
              onClick={handleCopy}
            >
              <Link className="size-4" />
              <fbt desc="Button to copy the calendar subscription URL to clipboard">
                Copy link
              </fbt>
            </Button>
          </section>

          <section>
            <SectionHeader variant="settings">
              <fbt desc="Section heading for calendar feed content settings">Include</fbt>
            </SectionHeader>
            <SettingsCard>
              <div className="grid grid-cols-[1fr_auto_auto] items-center px-4 py-2 text-xs text-muted-foreground">
                <span />
                <span className="w-14 text-center">
                  {fbt('Going', 'Column header for the Going status toggle in calendar settings')}
                </span>
                <span className="w-14 text-center">
                  <fbt desc="Column header for the Interested status toggle in calendar settings">Int.</fbt>
                </span>
              </div>

              <CalendarIncludeRow
                label={fbt('My events', 'Row label for the user\'s own events in calendar feed settings')}
                goingChecked={hasStatus(include.myEvents, 'going')}
                interestedChecked={hasStatus(include.myEvents, 'interested')}
                onGoingChange={() => handleStatusToggle('myEvents', 'going')}
                onInterestedChange={() => handleStatusToggle('myEvents', 'interested')}
              />

              {hasFriends && (
                <CalendarIncludeRow
                  label={fbt('Friends', 'Row label for friends\' events in calendar feed settings')}
                  goingChecked={hasStatus(include.friendEvents, 'going')}
                  interestedChecked={hasStatus(include.friendEvents, 'interested')}
                  onGoingChange={() => handleStatusToggle('friendEvents', 'going')}
                  onInterestedChange={() => handleStatusToggle('friendEvents', 'interested')}
                />
              )}

              {hasGroups && (
                <>
                  <div
                    className="grid grid-cols-[auto_1fr_auto] items-center px-4 py-3 cursor-pointer transition-colors hover:bg-muted/50"
                    onClick={() => setGroupsExpanded(v => !v)}
                  >
                    <ChevronRight className={`size-4 text-muted-foreground mr-2 transition-transform ${groupsExpanded ? 'rotate-90' : ''}`} />
                    <span className="text-sm text-foreground">
                      {fbt('Groups', 'Row label for group events in calendar feed settings, acts as default for all groups')}
                    </span>
                    <div className="w-14 flex justify-center" onClick={e => e.stopPropagation()}>
                      <Switch size="sm" checked={groupDefault} onCheckedChange={handleGroupDefaultToggle} className="cursor-pointer" />
                    </div>
                  </div>

                  {groupsExpanded && groups.map(g => (
                    <div
                      key={g.id}
                      className="grid grid-cols-[1fr_auto] items-center pl-10 pr-4 py-2.5"
                    >
                      <span className="text-sm text-muted-foreground truncate">{g.name}</span>
                      <div className="w-14 flex justify-center">
                        <Switch size="sm" checked={isGroupEnabled(g.id)} onCheckedChange={() => handleGroupToggle(g.id)} className="cursor-pointer" />
                      </div>
                    </div>
                  ))}
                </>
              )}
            </SettingsCard>
          </section>

          <section>
            <SectionHeader variant="settings">
              <fbt desc="Section heading for calendar display/behavior settings">Display</fbt>
            </SectionHeader>
            <SettingsCard>
              <SettingsRowBase
                label={fbt(
                  'All-day events',
                  'Label for the all-day events toggle in calendar display settings',
                )}
                description={fbt(
                  'Show events as full-day blocks instead of timed entries',
                  'Description explaining what the all-day events toggle does',
                )}
                trailing={
                  <Switch
                    size="sm"
                    checked={include.forceAllDay ?? false}
                    onCheckedChange={v => handleToggle({ forceAllDay: v }, 'force_all_day', v)}
                    className="cursor-pointer"
                  />
                }
              />
            </SettingsCard>
          </section>
        </div>
      )}
    </PageLayout>
  )
}

function AppleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  )
}

function GoogleCalendarIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  )
}

function OutlookIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path d="M24 7.387v10.478c0 .23-.08.424-.238.576a.806.806 0 0 1-.588.234h-8.55v-8.06l1.52 1.097a.27.27 0 0 0 .32 0L24 6.386v1.001zM16.078 10.29l7.55-5.452a.78.78 0 0 0-.452-.15h-8.55v4.99l1.452.612zM14.616 4.688H0V19.69l14.616-1.634V4.688zm-4.06 11.388c-.648.564-1.444.846-2.39.846h-.052c-.933 0-1.72-.278-2.357-.834-.637-.556-.955-1.39-.955-2.502 0-1.164.33-2.038.988-2.622.659-.584 1.457-.876 2.394-.876h.052c.945 0 1.736.284 2.372.852.637.568.955 1.418.955 2.55 0 1.14-.336 2.022-1.007 2.586zm-.56-4.392c-.382-.422-.896-.634-1.54-.634h-.04c-.657 0-1.177.214-1.56.644-.383.43-.575 1.056-.575 1.878 0 .838.19 1.476.568 1.914.378.438.895.658 1.549.658h.04c.666 0 1.188-.216 1.564-.65.376-.434.564-1.072.564-1.914 0-.83-.19-1.466-.57-1.896z" fill="#0078D4" />
    </svg>
  )
}

function CalendarIncludeRow({
  label,
  goingChecked,
  interestedChecked,
  onGoingChange,
  onInterestedChange,
}: {
  label: string
  goingChecked: boolean
  interestedChecked: boolean
  onGoingChange: () => void
  onInterestedChange: () => void
}) {
  return (
    <div className="grid grid-cols-[1fr_auto_auto] items-center px-4 py-3">
      <span className="text-sm text-foreground truncate">{label}</span>
      <div className="w-14 flex justify-center">
        <Switch size="sm" checked={goingChecked} onCheckedChange={onGoingChange} className="cursor-pointer" />
      </div>
      <div className="w-14 flex justify-center">
        <Switch size="sm" checked={interestedChecked} onCheckedChange={onInterestedChange} className="cursor-pointer" />
      </div>
    </div>
  )
}
